import React, { useEffect } from 'react';

import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';

import { useDocumentTreeInputLock } from '../context/DocumentTreeContext';

interface ActionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description: string;
  confirmButtonText: string;
  onConfirm: () => void;
  errorMessage?: string | null;
  inputValue?: string;
  onInputChange?: (value: string) => void;
  inputPlaceholder?: string;
  isConfirmDisabled?: boolean;
  isDangerousAction?: boolean;
  children?: React.ReactNode;
}

export const ActionDialog = ({
  isOpen,
  onClose,
  title,
  description,
  confirmButtonText,
  onConfirm,
  errorMessage = null,
  inputValue,
  onInputChange,
  inputPlaceholder,
  isConfirmDisabled = false,
  isDangerousAction = false,
  children,
}: ActionDialogProps) => {
  const { beginTextInputInteraction, endTextInputInteraction } =
    useDocumentTreeInputLock();

  const shouldLockDrag =
    isOpen && inputValue !== undefined && onInputChange !== undefined;

  useEffect(() => {
    if (!shouldLockDrag) {
      return () => {
        // No-op cleanup when drag locking is not needed
      };
    }

    beginTextInputInteraction();

    return () => {
      endTextInputInteraction();
    };
  }, [beginTextInputInteraction, endTextInputInteraction, shouldLockDrag]);

  // Prevent dialog from closing when clicking on Headless UI portaled elements (like Combobox options)
  const handleDialogClose = () => {
    // Check if there's an active Headless UI popup (Combobox, Listbox, etc.)
    const activePopup = document.querySelector(
      '[data-headlessui-state="open"][role="listbox"]',
    );
    if (activePopup) {
      return;
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleDialogClose}>
      <div>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>
          {description}
          {errorMessage && <p className="text-red-500 mt-1">{errorMessage}</p>}
        </DialogDescription>
        <DialogBody>
          {inputValue !== undefined && onInputChange && (
            <input
              type="text"
              value={inputValue}
              onChange={(e) => onInputChange(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
              className="w-full px-3 py-2 border border-zinc-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={inputPlaceholder}
            />
          )}
          {children}
        </DialogBody>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={onConfirm}
            disabled={isConfirmDisabled}
            className={isDangerousAction ? 'text-red-600' : undefined}
          >
            {confirmButtonText}
          </Button>
        </DialogActions>
      </div>
    </Dialog>
  );
};

export default ActionDialog;
